import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';

import { FeatureRequest, FeatureComment } from '../../services/FeatureRequestService';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Send from '@mui/icons-material/Send';
import relativeTime from 'dayjs/plugin/relativeTime';
import { UserData } from '../../services/UserService';

dayjs.extend(relativeTime);

interface FeatureCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRequest: FeatureRequest | null;
  comments: FeatureComment[];
  newComment: string;
  setNewComment: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: () => void;
  user: UserData | null;
}

const FeatureCommentsDialog: React.FC<FeatureCommentsDialogProps> = ({
  open,
  onClose,
  selectedRequest,
  comments,
  newComment,
  setNewComment,
  onSubmit,
  user
}) => {
  const intl = useIntl();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormattedMessage id="featureRequests.comments" />
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedRequest && (
          <>
            <Typography variant="h6">{selectedRequest.title}</Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              <FormattedMessage 
                id="featureRequests.postedBy" 
                values={{ 
                  name: selectedRequest.userName || intl.formatMessage({ id: "common.anonymous" }),
                  time: dayjs(selectedRequest.createdAt).fromNow()
                }}
              />
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <List>
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <React.Fragment key={comment.id}>
                    <ListItem alignItems="flex-start">
                      <Box width="100%">
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle2">
                            {comment.userName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {dayjs(comment.createdAt).fromNow()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {comment.comment}
                        </Typography>
                      </Box>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" py={3}>
                  <FormattedMessage id="featureRequests.noComments" />
                </Typography>
              )}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, flexDirection: 'column', alignItems: 'stretch' }}>
        <Box display="flex" gap={1}>
          <TextField
            placeholder={intl.formatMessage({ id: "featureRequests.addComment" })}
            fullWidth
            variant="outlined"
            size="small"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={!user}
          />
          <Button 
            variant="contained" 
            color="primary"
            endIcon={<Send />}
            onClick={onSubmit}
            disabled={!user || !newComment.trim()}
          >
            <FormattedMessage id="common.post" />
          </Button>
        </Box>
        {!user && (
          <Typography variant="caption" color="error" mt={1}>
            <FormattedMessage id="featureRequests.loginToComment" />
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeatureCommentsDialog;